import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppShell from './components/AppShell';
import AppErrorBoundary from './components/errors/AppErrorBoundary';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import ArticlePage from './pages/ArticlePage';
import ContactPage from './pages/ContactPage';
import ScheduleDemoPage from './pages/ScheduleDemoPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import {
  DashboardPage,
  EmployeesPage,
  EmployeeProfilePage,
  ImportsPage,
  RiskMapPage,
  ActionItemsPage,
  ReportsPage,
  SettingsPage,
} from './routing/lazyPages';
import DocumentHead from './components/DocumentHead';
import AppRouteNotFound from './routing/AppRouteFallbacks';
import { RouteDiagnostics, RoutePage } from './routing/RoutePage';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PublicRoutePage({ name, children }) {
  return (
    <AppErrorBoundary context={`public:${name}`} showDashboardLink={false}>
      {children}
    </AppErrorBoundary>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoutePage name="landing">
            <LandingPage />
          </PublicRoutePage>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoutePage name="login">
            <LoginPage />
          </PublicRoutePage>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoutePage name="signup">
            <SignupPage />
          </PublicRoutePage>
        }
      />
      <Route path="/about" element={<PublicRoutePage name="about"><AboutPage /></PublicRoutePage>} />
      <Route path="/blog" element={<PublicRoutePage name="blog"><BlogPage /></PublicRoutePage>} />
      <Route path="/blog/:slug" element={<PublicRoutePage name="article"><ArticlePage /></PublicRoutePage>} />
      <Route path="/contact" element={<PublicRoutePage name="contact"><ContactPage /></PublicRoutePage>} />
      <Route path="/schedule-demo" element={<PublicRoutePage name="schedule-demo"><ScheduleDemoPage /></PublicRoutePage>} />
      <Route path="/privacy" element={<PublicRoutePage name="privacy"><PrivacyPage /></PublicRoutePage>} />
      <Route path="/terms" element={<PublicRoutePage name="terms"><TermsPage /></PublicRoutePage>} />
      <Route
        path="/app"
        element={
          <AppErrorBoundary context="app-shell">
            <AppShell />
          </AppErrorBoundary>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<RoutePage name="dashboard"><DashboardPage /></RoutePage>} />
        <Route path="employees/:id" element={<RoutePage name="employee-profile"><EmployeeProfilePage /></RoutePage>} />
        <Route path="employees" element={<RoutePage name="employees"><EmployeesPage /></RoutePage>} />
        <Route path="imports" element={<RoutePage name="imports"><ImportsPage /></RoutePage>} />
        <Route path="risk" element={<RoutePage name="risk"><RiskMapPage /></RoutePage>} />
        <Route path="actions" element={<RoutePage name="actions"><ActionItemsPage /></RoutePage>} />
        <Route path="reports" element={<RoutePage name="reports"><ReportsPage /></RoutePage>} />
        <Route path="settings" element={<RoutePage name="settings"><SettingsPage /></RoutePage>} />
        {/* Legacy redirects */}
        <Route path="risks" element={<Navigate to="/app/risk" replace />} />
        <Route path="analytics" element={<Navigate to="/app/risk" replace />} />
        <Route path="exports" element={<Navigate to="/app/reports" replace />} />
        <Route path="*" element={<AppRouteNotFound />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppErrorBoundary context="root">
          <AuthProvider>
            <DocumentHead />
            <ScrollToTop />
            <RouteDiagnostics />
            <AppRoutes />
          </AuthProvider>
        </AppErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
